import React from 'react';
import { Calendar, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * AddToCalendar - Button to add call deadline to Google Calendar
 * Only available for authenticated users
 */
const AddToCalendar = ({ call, user }) => {
  // Only show for authenticated users
  if (!user) {
    return null;
  }

  // Don't show for continuous flow calls (no deadline)
  if (call.continuous_flow || !call.deadline) {
    return null;
  }

  const formatDateForGoogle = (dateStr) => {
    // Convert to YYYYMMDD format for Google Calendar
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const generateGoogleCalendarUrl = () => {
    const baseUrl = 'https://calendar.google.com/calendar/render';
    
    // Event title
    const title = encodeURIComponent(`Prazo: ${call.title} - ${call.journal_name}`);
    
    // Deadline date (all-day event)
    const deadlineDate = formatDateForGoogle(call.deadline);
    // For all-day events, end date is the next day
    const nextDay = new Date(call.deadline);
    nextDay.setDate(nextDay.getDate() + 1);
    const endDate = formatDateForGoogle(nextDay.toISOString());
    
    // Description
    const description = encodeURIComponent(
      `Chamada para publicação: ${call.title}\n\n` +
      `Revista: ${call.journal_name}\n` +
      `Classificação QUALIS: ${call.qualis_classification}\n` +
      `Áreas: ${call.knowledge_areas?.join(', ') || 'Não especificada'}\n\n` +
      `Link para submissão: ${call.submission_url || 'Ver detalhes no site'}\n\n` +
      `Mais informações: ${window.location.href}`
    );
    
    // Location (submission URL)
    const location = encodeURIComponent(call.submission_url || 'https://queropublicar.org');
    
    const url = `${baseUrl}?action=TEMPLATE` +
      `&text=${title}` +
      `&dates=${deadlineDate}/${endDate}` +
      `&details=${description}` +
      `&location=${location}` +
      `&sf=true`;
    
    return url;
  };

  const handleAddToCalendar = () => {
    const url = generateGoogleCalendarUrl();
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success('Abrindo Google Agenda...');
  };

  // Calculate days until deadline
  const getDaysUntil = () => {
    const now = new Date();
    const deadline = new Date(call.deadline);
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntil = getDaysUntil();
  const isUrgent = daysUntil <= 7 && daysUntil > 0;
  const isPast = daysUntil < 0;

  if (isPast) {
    return null; // Don't show for past deadlines
  }

  return (
    <Button
      onClick={handleAddToCalendar}
      variant="outline"
      className={`gap-2 ${isUrgent ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
    >
      <Calendar className="h-4 w-4" />
      Adicionar à Agenda
      <ExternalLink className="h-3 w-3" />
    </Button>
  );
};

export default AddToCalendar;
